/**
 * DeliverySettingsSection Component
 *
 * Email delivery settings section (without Card wrapper) with:
 * - Timezone configuration
 * - Quiet hours settings
 *
 * Designed to be used within NotificationScheduleCard for unified layout.
 */

import {
  BlockStack,
  InlineStack,
  Text,
  Box,
  Icon,
  Select,
} from '@shopify/polaris';
import { ClockIcon } from '@shopify/polaris-icons';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import { TIME_OPTIONS } from '../../../constants/time-options';
import { Toggle } from '../../ui';

export interface DeliverySettingsSectionProps {
  /** Current timezone setting */
  timezone: string;
  /** Handler for timezone change */
  onTimezoneChange: (timezone: string) => void;
  /** Whether quiet hours are enabled */
  quietHoursEnabled: boolean;
  /** Quiet hours start time */
  quietHoursStart: string;
  /** Quiet hours end time */
  quietHoursEnd: string;
  /** Handler for quiet hours enabled change */
  onQuietHoursEnabledChange: (enabled: boolean) => void;
  /** Handler for quiet hours start time change */
  onQuietHoursStartChange: (time: string) => void;
  /** Handler for quiet hours end time change */
  onQuietHoursEndChange: (time: string) => void;
}

export function DeliverySettingsSection({
  timezone,
  onTimezoneChange,
  quietHoursEnabled,
  quietHoursStart,
  quietHoursEnd,
  onQuietHoursEnabledChange,
  onQuietHoursStartChange,
  onQuietHoursEndChange,
}: DeliverySettingsSectionProps) {
  return (
    <BlockStack gap="400">
      {/* Header */}
      <InlineStack gap="200" blockAlign="start" wrap={false}>
        <Box paddingBlockStart="050">
          <Icon source={ClockIcon} tone="subdued" />
        </Box>
        <BlockStack gap="100">
          <Text as="h3" variant="headingMd">
            Delivery settings
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Configure when notifications are delivered
          </Text>
        </BlockStack>
      </InlineStack>

      {/* Content */}
      <BlockStack gap="300">
        {/* Timezone - Tinted background */}
        <Box background="bg-surface-secondary" padding="300" borderRadius="200">
          <Select
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            value={timezone}
            onChange={onTimezoneChange}
          />
        </Box>

        {/* Quiet Hours - Tinted background */}
        <Box background="bg-surface-secondary" padding="300" borderRadius="200">
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="start" wrap={false}>
              <BlockStack gap="100">
                <Text as="p" variant="bodyMd">
                  Enable quiet hours
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {quietHoursEnabled
                    ? `Notifications paused ${quietHoursStart} to ${quietHoursEnd}`
                    : 'Pause non-critical notifications during specified hours'}
                </Text>
              </BlockStack>
              <Toggle
                label="Enable quiet hours"
                enabled={quietHoursEnabled}
                onChange={onQuietHoursEnabledChange}
              />
            </InlineStack>

            {quietHoursEnabled && (
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <Box minWidth="120px">
                  <Select
                    label="Start time"
                    labelHidden
                    options={TIME_OPTIONS}
                    value={quietHoursStart}
                    onChange={onQuietHoursStartChange}
                  />
                </Box>
                <Text as="span" variant="bodySm" tone="subdued">
                  to
                </Text>
                <Box minWidth="120px">
                  <Select
                    label="End time"
                    labelHidden
                    options={TIME_OPTIONS}
                    value={quietHoursEnd}
                    onChange={onQuietHoursEndChange}
                  />
                </Box>
              </InlineStack>
            )}
          </BlockStack>
        </Box>
      </BlockStack>
    </BlockStack>
  );
}
